import { createContext, useCallback, useContext, useMemo, type ReactNode } from 'react';
import { DEFAULT_LOCALE, type Locale } from './types';
import { messages, type MessageKey } from './messages';

interface I18n {
    locale: Locale;
    t: (key: MessageKey, params?: Record<string, string | number>) => string;
}

const I18nContext = createContext<Locale>(DEFAULT_LOCALE);

export function I18nProvider({ locale, children }: { locale: Locale; children: ReactNode }) {
    return <I18nContext.Provider value={locale}>{children}</I18nContext.Provider>;
}

export function useTranslation(): I18n {
    const locale = useContext(I18nContext);
    const t = useCallback(
        (key: MessageKey, params?: Record<string, string | number>) =>
            translate(key, locale, params),
        [locale],
    );
    return useMemo(() => ({ locale, t }), [locale, t]);
}

/**
 * Render a message for the given locale. Falls back through the locale
 * chain (target → en_US) before giving up and surfacing the raw key — that
 * way a typo or a missing translation is visible in the UI rather than
 * silently rendering an empty string.
 */
export function translate(
    key: MessageKey,
    locale: Locale,
    params?: Record<string, string | number>,
): string {
    const template = messages[locale]?.[key] ?? messages[DEFAULT_LOCALE][key] ?? key;
    return interpolate(template, params);
}

function interpolate(template: string, params?: Record<string, string | number>): string {
    if (!params) return template;
    return template.replace(/\{(\w+)\}/g, (match, name: string) => {
        const v = params[name];
        return v === undefined || v === null ? match : String(v);
    });
}
